// React Query mutation hooks for Management Guidelines.
// Slice 4 added delete; Slice 6 adds create + update (Save Draft / Save).

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { managementGuidelinesApi } from '../api/management-guidelines.adapter';
import type {
  CreateGuidelineInput,
  DeleteGuidelineResult,
  ManagementGuidelineDetail,
  UpdateGuidelineInput,
} from '../types/management-guidelines.types';
import { managementGuidelineQueryKeys } from './use-guidelines';

// PRD §3.3 — deletes the guideline and (server-side) auto-unlinks any PRO
// items referencing the GID. The list query is invalidated on success so the
// table refreshes immediately. Errors propagate to the caller for toast
// handling because the confirm modal owns the surface.
export function useDeleteGuideline() {
  const qc = useQueryClient();
  return useMutation<DeleteGuidelineResult, Error, string>({
    mutationFn: (gid) => managementGuidelinesApi.remove(gid),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: managementGuidelineQueryKeys.list() });
    },
  });
}

// PRD §3.2 — Save Draft and Save (PUBLISHED) for new guidelines. BE assigns
// the GID. List + detail caches are invalidated so the table and any open
// edit panel reflect the new row.
export function useCreateGuideline() {
  const qc = useQueryClient();
  return useMutation<ManagementGuidelineDetail, Error, CreateGuidelineInput>({
    mutationFn: (input) => managementGuidelinesApi.create(input),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: managementGuidelineQueryKeys.list() });
      // Seed the detail cache so an immediate re-open skips the spinner.
      qc.setQueryData(managementGuidelineQueryKeys.detail(created.gid), created);
    },
  });
}

// PRD §3.2 / §3.4 — edits an existing guideline. BE enforces the
// PUBLISHED→DRAFT transition block (the UI also hides the Save Draft button
// for currently-PUBLISHED rows so this only catches direct API misuse).
export function useUpdateGuideline() {
  const qc = useQueryClient();
  return useMutation<
    ManagementGuidelineDetail,
    Error,
    { gid: string; input: UpdateGuidelineInput }
  >({
    mutationFn: ({ gid, input }) => managementGuidelinesApi.update(gid, input),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: managementGuidelineQueryKeys.list() });
      qc.setQueryData(managementGuidelineQueryKeys.detail(updated.gid), updated);
    },
  });
}
